import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { markdownToHtml } from "../lib/markdown-to-html.ts";
import { sanitizeMermaidSource } from "../lib/mermaid-sanitize.ts";

let mermaidThemeInitialized = false;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function initMermaidTheme(mermaid: typeof import("mermaid").default): Promise<void> {
  if (mermaidThemeInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: "base",
    securityLevel: "loose",
    themeVariables: {
      background: "#111827",
      primaryColor: "#fcd5b5",
      primaryTextColor: "#111827",
      primaryBorderColor: "#d97706",
      lineColor: "#a5b4fc",
      secondaryColor: "#0f766e",
      tertiaryColor: "#ddd6fe",
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    },
    flowchart: {
      useMaxWidth: true,
      htmlLabels: true,
      curve: "basis",
      diagramPadding: 20,
      nodeSpacing: 56,
      rankSpacing: 48,
    },
  });
  mermaidThemeInitialized = true;
}

@customElement("ead-skill-report-view")
export class EadSkillReportView extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
    }
    :host([embedded]) {
      height: auto;
      min-height: 0;
    }
    :host([embedded]) .report-shell {
      height: auto;
      overflow: visible;
      border: none;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
    }
    :host([embedded]) .ai-markdown-viewer {
      padding: 0 0 1rem;
    }
    .report-shell {
      height: 100%;
      overflow-y: auto;
      border: 1px solid rgba(148, 163, 184, 0.22);
      border-radius: 10px;
      background: linear-gradient(180deg, rgba(15, 15, 22, 0.98) 0%, rgba(8, 8, 12, 0.99) 100%);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.04);
    }
    .ai-markdown-viewer {
      padding: 2rem 2.5rem 2.5rem;
      color: #e2e8f0;
      font-size: 0.95rem;
      line-height: 1.65;
    }
    .help-tip-h1 {
      font-size: 1.35rem;
      font-weight: 700;
      color: #f8fafc;
      margin: 0 0 0.85rem;
      letter-spacing: -0.02em;
    }
    .help-tip-h2 {
      font-size: 1.05rem;
      font-weight: 650;
      color: #f1f5f9;
      margin: 1.75rem 0 0.85rem;
      padding-bottom: 0.35rem;
      border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }
    .help-tip-h3 {
      font-size: 0.98rem;
      font-weight: 600;
      color: #e2e8f0;
      margin: 1.25rem 0 0.65rem;
    }
    .help-tip-p {
      margin: 0.65rem 0;
      color: #cbd5e1;
    }
    .help-tip-hr {
      border: none;
      border-top: 1px solid rgba(148, 163, 184, 0.2);
      margin: 1.25rem 0;
    }
    .help-tip-ul, .help-tip-ol {
      margin: 0.65rem 0 0.85rem 1.25rem;
      color: #cbd5e1;
    }
    .help-tip-li {
      margin: 0.35rem 0;
    }
    .help-tip-code {
      font-family: "JetBrains Mono", "SF Mono", Monaco, monospace;
      font-size: 0.88em;
      background: rgba(15, 23, 42, 0.85);
      border: 1px solid rgba(148, 163, 184, 0.15);
      border-radius: 4px;
      padding: 0.12em 0.35em;
      color: #e2e8f0;
    }
    pre.help-tip-code {
      display: block;
      background: #0b1220;
      border: 1px solid rgba(148, 163, 184, 0.18);
      border-radius: 8px;
      padding: 1rem 1.15rem;
      overflow-x: auto;
      margin: 1rem 0;
      box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.25);
    }
    pre.help-tip-code code {
      background: transparent;
      border: none;
      padding: 0;
      color: #e2e8f0;
      font-size: 0.9rem;
      line-height: 1.55;
      white-space: pre-wrap;
    }
    .markdown-mermaid-rendered {
      overflow: auto;
      margin: 1rem 0 0.5rem;
      padding: 1rem;
      border: 1px solid rgba(167, 139, 250, 0.22);
      border-radius: 0.65rem;
      background: #0f172a;
    }
    .markdown-mermaid-rendered svg {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 0 auto;
    }
    .markdown-mermaid-details {
      margin: 0 0 1.25rem;
      color: #94a3b8;
      font-size: 0.82rem;
    }
    .markdown-mermaid-details summary {
      cursor: pointer;
      user-select: none;
    }
    .help-tip-link {
      color: #38bdf8;
      text-decoration: none;
    }
    .help-tip-link:hover {
      color: #7dd3fc;
      text-decoration: underline;
    }
    .ai-markdown-viewer strong {
      font-weight: 600;
      color: #f1f5f9;
    }
    .ai-markdown-viewer em {
      color: #94a3b8;
    }
  `;

  @property({ type: String }) markdown = "";
  @property({ type: Boolean }) embedded = false;

  private _container: HTMLDivElement | null = null;
  private _renderRun = 0;

  updated(changed: Map<string, unknown>): void {
    if (changed.has("embedded")) {
      if (this.embedded) {
        this.setAttribute("embedded", "");
      } else {
        this.removeAttribute("embedded");
      }
    }
    if (changed.has("markdown")) {
      void this._renderReport();
    }
  }

  firstUpdated(): void {
    if (this.embedded) this.setAttribute("embedded", "");
    this._container = this.renderRoot.querySelector(".ai-markdown-viewer") as HTMLDivElement | null;
    void this._renderReport();
  }

  private async _renderReport(): Promise<void> {
    const container = this._container;
    if (!container) return;
    const runId = ++this._renderRun;
    const htmlContent = markdownToHtml(this.markdown || "");
    container.innerHTML = htmlContent;

    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    if (runId !== this._renderRun) return;

    const blocks = Array.from(container.querySelectorAll<HTMLElement>(".markdown-mermaid-block[data-mermaid]"));
    if (!blocks.length) return;

    try {
      const mermaid = (await import("mermaid")).default;
      if (runId !== this._renderRun) return;
      await initMermaidTheme(mermaid);

      await Promise.all(
        blocks.map(async (block, index) => {
          const encoded = block.dataset.mermaid || "";
          const graph = sanitizeMermaidSource(decodeURIComponent(encoded));
          const id = `ead-skill-report-mmd-${runId}-${index}`;
          const { svg } = await mermaid.render(id, graph);
          if (runId !== this._renderRun) return;
          block.innerHTML = `<div class="markdown-mermaid-rendered">${svg}</div><details class="markdown-mermaid-details"><summary>Mermaid source</summary><pre class="help-tip-code markdown-mermaid-source"><code>${escapeHtml(graph)}</code></pre></details>`;
        }),
      );
    } catch (err) {
      console.warn("[ead-skill-report-view] Mermaid render failed:", err);
    }
  }

  render() {
    return html`
      <div class="report-shell">
        <div class="ai-markdown-viewer"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-skill-report-view": EadSkillReportView;
  }
}
